// lib/services/compute/base_slot.ts

export enum SlotState {
  Idle = 0,        // recyclable, no data, available for a new write
  Recording = 1,   // producer is filling the slot; GPU work may be recorded
  Pending = 2,     // GPU work submitted, async operation in flight (mapAsync)
  Ready = 3,       // data is complete and consumable; available for lease
  Leased = 4,      // a consumer holds it; cannot be recycled or stolen
}

const IS_DEVELOPMENT =
  typeof process !== "undefined" &&
  (process.env?.NODE_ENV ?? "development") !== "production";

/**
 * Base class for all pool slots. Owns its own state machine.
 *
 * Concrete subclasses attach domain-specific fields (GPU buffers, typed
 * array views, etc.) and may override recycle() to clear those fields
 * when the slot returns to Idle.
 *
 * The state transitions are linear; subclasses should call the protected
 * transition_to() method rather than mutating state directly so that
 * invalid transitions can be caught in development builds.
 */
export default class BaseSlot {
  state: SlotState = SlotState.Idle;
  sequence: number = 0;
  timestampSec: number = 0;

  // Optional external lease this slot is holding. Used by CPU readback
  // slots that hold a lease on a GPU producer pool while mapAsync is in
  // flight. The callback is invoked when the slot transitions out of
  // Pending (either to Ready on success, or to Idle on failure).
  external_lease_release: (() => void) | null = null;

  // True for slots that should ignore lease bookkeeping (static library
  // slots). The pool short-circuits acquire/release for these.
  readonly permanent_ready: boolean;

  constructor(permanent_ready: boolean = false) {
    this.permanent_ready = permanent_ready;
    if (permanent_ready) {
      this.state = SlotState.Ready;
    }
  }

  /**
   * Returns the slot to Idle and releases any external lease.
   * Called by the pool when the slot can be reused.
   * Subclasses may override to clear domain-specific fields.
   */
  recycle(): void {
    if (this.permanent_ready) {
      // Permanent slots are never recycled.
      return;
    }

    this.release_external_lease();
    this.state = SlotState.Idle;
    this.sequence = 0;
    this.timestampSec = 0;
  }

  /**
   * Releases any held external lease. Safe to call repeatedly.
   */
  release_external_lease(): void {
    if (this.external_lease_release) {
      const release = this.external_lease_release;
      this.external_lease_release = null;
      release();
    }
  }

  /**
   * Protected transition guard. Concrete subclasses call this to move
   * between states with a sanity check in development.
   */
  protected transition_to(next: SlotState): void {
    if (this.permanent_ready) {
      if (IS_DEVELOPMENT && next !== SlotState.Ready) {
        console.error(
          `[BaseSlot] permanent slot cannot transition from Ready to ${SlotState[next]}`
        );
      }
      return;
    }

    if (IS_DEVELOPMENT && !this.is_valid_transition(this.state, next)) {
      console.error(
        `[BaseSlot] invalid transition: ${SlotState[this.state]} -> ${SlotState[next]}`
      );
    }
    this.state = next;
  }

  private is_valid_transition(from: SlotState, to: SlotState): boolean {
    switch (from) {
      case SlotState.Idle:
        return to === SlotState.Recording;
      case SlotState.Recording:
        return to === SlotState.Pending || to === SlotState.Ready || to === SlotState.Idle;
      case SlotState.Pending:
        return to === SlotState.Ready || to === SlotState.Idle;
      case SlotState.Ready:
        return to === SlotState.Leased || to === SlotState.Idle;
      case SlotState.Leased:
        return to === SlotState.Ready || to === SlotState.Idle;
      default:
        return false;
    }
  }
}